import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

class ApiService {
  constructor() {
    this.axios = axios.create({
      baseURL: API_BASE,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to attach auth token
    this.axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('era_auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Asset endpoints
  async getCryptoAssets() {
    try {
      const response = await this.axios.get('/assets/crypto');
      
      if (!response.data || !Array.isArray(response.data.assets)) {
        throw new Error('Invalid crypto assets response format');
      }
      
      return response.data.assets;
    } catch (error) {
      console.error('Error fetching crypto assets:', error);
      // Return empty array to prevent runtime errors
      return [];
    }
  }

  async getTop20Cryptos() {
    const response = await this.axios.get('/crypto/top20');

    if (!response.data || !Array.isArray(response.data.assets)) {
      throw new Error('Invalid top 20 cryptos response format');
    }

    return response.data.assets;
  }

  async getCryptoDetails(cryptoId) {
    try {
      const token = localStorage.getItem("era_auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await this.axios.get(`/crypto/${cryptoId}/details`, { headers });
      return response.data;
    } catch (error) {
      console.error(`Error fetching details for ${cryptoId}:`, error);
      throw error;
    }
  }

  async getCryptoTechnicalAnalysis(cryptoId, cryptoName) {
    try {
      const response = await this.axios.get(`/crypto/${cryptoId}/technical-analysis`, {
        params: { crypto_name: cryptoName }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching technical analysis for ${cryptoId}:`, error);
      throw error;
    }
  }

  async getCryptoChartData(cryptoId, days = 30) {
    try {
      const response = await this.axios.get(`/crypto/${cryptoId}/chart`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching chart data for ${cryptoId}:`, error);
      throw error;
    }
  }

  async getEquityAssets() {
    try {
      const response = await this.axios.get('/assets/equities');
      
      if (!response.data || !Array.isArray(response.data.assets)) {
        throw new Error('Invalid equity assets response format');
      }
      
      return response.data.assets;
    } catch (error) {
      console.error('Error fetching equity assets:', error);
      // Return empty array to prevent runtime errors
      return [];
    }
  }

  async getCommodityAssets() {
    try {
      const response = await this.axios.get('/assets/commodities');
      
      if (!response.data || !Array.isArray(response.data.assets)) {
        throw new Error('Invalid commodity assets response format');
      }
      
      return response.data.assets;
    } catch (error) {
      console.error('Error fetching commodity assets:', error);
      // Return empty array to prevent runtime errors
      return [];
    }
  }

  // Market data endpoints
  async getMarketOverview() {
    try {
      const response = await this.axios.get('/market-overview');
      
      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid market overview response format');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching market overview:', error);
      
      // Return fallback data structure to prevent runtime errors
      return {
        crypto: { total_market_cap: 0, change24h: 0 },
        equities: { total_market_cap: 0, change24h: 0 },
        commodities: { total_market_cap: 0, change24h: 0 },
        liquidity_index: { value: 0, change24h: 0 },
        vix_index: 18.5,
        fear_greed_index: 50
      };
    }
  }

  async getNews(limit = 10, category = null) {
    try {
      const params = { limit };
      if (category) params.category = category;
      
      const response = await this.axios.get('/news', { params });
      return response.data.news;
    } catch (error) {
      console.error('Error fetching news:', error);
      throw error;
    }
  }

  async getDailyRecap() {
    try {
      const response = await this.axios.get('/daily-recap');
      return response.data;
    } catch (error) {
      console.error('Error fetching daily recap:', error);
      throw error;
    }
  }

  async getSentiment() {
    try {
      const response = await this.axios.get('/sentiment');
      return response.data;
    } catch (error) {
      console.error('Error fetching sentiment:', error);
      throw error;
    }
  }

  async getOverallSentiment() {
    try {
      const response = await this.axios.get('/sentiment/overall/latest');
      return response.data;
    } catch (error) {
      console.error('Error fetching overall sentiment:', error);
      return null;
    }
  }

  // AI Chat endpoints
  async chatWithAI(message, sessionId = null) {
    try {
      const response = await this.axios.post('/ai/chat', {
        message,
        session_id: sessionId
      });
      return response.data;
    } catch (error) {
      console.error('Error chatting with AI:', error);
      throw error;
    }
  }

  async createAISession() {
    try {
      const response = await this.axios.post('/ai/session');
      return response.data.session_id;
    } catch (error) {
      console.error('Error creating AI session:', error);
      throw error;
    }
  }

  async getInvestmentPrompts() {
    try {
      const response = await this.axios.get('/ai/prompts');
      return response.data.prompts;
    } catch (error) {
      console.error('Error fetching investment prompts:', error);
      return [];
    }
  }

  // Utility method to check API health
  async checkHealth() {
    try {
      const response = await this.axios.get('/');
      return response.data;
    } catch (error) {
      console.error('API health check failed:', error);
      throw error;
    }
  }

  // Themes API endpoints
  async getAllThemes() {
    try {
      const response = await this.axios.get('/themes');
      return response.data.themes;
    } catch (error) {
      console.error('Error fetching themes:', error);
      throw error;
    }
  }

  async getThemeDetails(themeName) {
    try {
      const response = await this.axios.get(`/themes/${encodeURIComponent(themeName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching theme ${themeName}:`, error);
      throw error;
    }
  }

  async createTheme(themeName, stocks) {
    try {
      const response = await this.axios.post('/themes', {
        name: themeName,
        stocks: stocks
      });
      return response.data;
    } catch (error) {
      console.error('Error creating theme:', error);
      throw error;
    }
  }

  async updateTheme(themeName, stocks) {
    try {
      const response = await this.axios.put(`/themes/${encodeURIComponent(themeName)}`, {
        stocks: stocks
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating theme ${themeName}:`, error);
      throw error;
    }
  }

  async deleteTheme(themeName) {
    try {
      const response = await this.axios.delete(`/themes/${encodeURIComponent(themeName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting theme ${themeName}:`, error);
      throw error;
    }
  }
  // Usage tracking endpoints
  async getMyUsage() {
    try {
      const token = localStorage.getItem("era_auth_token");
      if (!token) return null;
      const response = await this.axios.get('/usage/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching usage:', error);
      return null;
    }
  }

  async getUsageData(feature) {
    try {
      const allUsage = await this.getMyUsage();
      if (!allUsage || !allUsage.features) return null;
      const featureData = allUsage.features.find(f => f.feature === feature);
      return featureData || { feature, used: 0, limit: 4 };
    } catch (error) {
      return null;
    }
  }

  async trackUsage(feature) {
    try {
      const token = localStorage.getItem("era_auth_token");
      if (!token) return null;
      const response = await this.axios.post('/usage/track',
        { feature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error tracking usage:', error);
      return null;
    }
  }

  async createCommunityPost(content, tags = []) {
    const token = localStorage.getItem("era_auth_token");
    if (!token) throw new Error('Login required');
    const response = await this.axios.post('/community/posts',
      { content, tags },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }

  // Single Stocks (gated: admin/reviewer/pro_max) — request interceptor attaches the JWT
  async getSingleStocksUniverse() {
    const response = await this.axios.get('/single-stocks/universe');
    return response.data;
  }

  async getSingleStock(ticker) {
    const response = await this.axios.get(`/single-stocks/stocks/${encodeURIComponent(ticker)}`);
    return response.data;
  }

  async getBaseRates() {
    const response = await this.axios.get('/single-stocks/base-rates');
    return response.data;
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;